import { CoreErrorCause, CoreError } from "../../../domain/models/CoreError";

export class AuthErrorConverter {
  static domainModel(fromExternalModel: unknown): CoreError {
    if (!(fromExternalModel instanceof Error)) {
      return new CoreError({
        cause: CoreErrorCause.Unknown,
        underlyingException: fromExternalModel,
        context: `An Error that is not Firebase Auth error happened: ${fromExternalModel}`,
      });
    }

    if (!("code" in fromExternalModel)) {
      return new CoreError({
        cause: CoreErrorCause.Unknown,
        underlyingException: fromExternalModel,
        context: `An undefined Error happened: ${fromExternalModel.message}`,
      });
    }

    const firebaseError = fromExternalModel as Error & { code: string };

    switch (firebaseError.code) {
      case "auth/invalid-email":
        return new CoreError({
          cause: CoreErrorCause.InvalidCredential,
          context: "The email address is badly formatted.",
          underlyingException: firebaseError,
          // Expected user-input outcome — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/user-disabled":
        return new CoreError({
          cause: CoreErrorCause.Forbidden,
          context:
            "The user corresponding to the given email has been disabled.",
          underlyingException: firebaseError,
        });
      case "auth/user-not-found":
        return new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: "There is no user corresponding to the given email.",
          underlyingException: firebaseError,
          // Expected user-input outcome — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/wrong-password":
        return new CoreError({
          cause: CoreErrorCause.InvalidCredential,
          context:
            "The password is invalid or the account does not have a password set.",
          underlyingException: firebaseError,
          // Expected user-input outcome — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/email-already-in-use":
        return new CoreError({
          cause: CoreErrorCause.EmailAlreadyInUse,
          context: "The email address is already in use by another account.",
          underlyingException: firebaseError,
          // Expected user-flow outcome (attempting signup with an existing account).
          // Keep user feedback in UI, but avoid polluting Sentry error issues.
          reportToSentry: false,
        });
      case "auth/operation-not-allowed":
        return new CoreError({
          cause: CoreErrorCause.Forbidden,
          context: "This operation is not allowed. Please contact support.",
          underlyingException: firebaseError,
        });
      case "auth/weak-password":
        return new CoreError({
          cause: CoreErrorCause.WeakPassword,
          context: "The password provided is too weak.",
          underlyingException: firebaseError,
          // Expected user-input outcome — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/too-many-requests":
        return new CoreError({
          cause: CoreErrorCause.Forbidden,
          context: "Too many requests. Please try again later.",
          underlyingException: firebaseError,
          // Expected throttling outcome — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/requires-recent-login":
        return new CoreError({
          cause: CoreErrorCause.RequireRecentAuthentication,
          context:
            "This operation requires recent authentication. Please log in again.",
          underlyingException: firebaseError,
        });
      case "auth/network-request-failed":
        return new CoreError({
          cause: CoreErrorCause.NetworkConnection,
          context:
            "A network error occurred. Please check your connection and try again.",
          underlyingException: firebaseError,
          // Environmental (device offline) — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/invalid-credential":
        return new CoreError({
          cause: CoreErrorCause.InvalidCredential,
          context: "The credentials provided are invalid.",
          underlyingException: firebaseError,
          // Expected user-input outcome — surfaced in UI, not a Sentry error.
          reportToSentry: false,
        });
      case "auth/user-token-expired":
      case "auth/invalid-user-token":
        return new CoreError({
          cause: CoreErrorCause.RequireRecentAuthentication,
          context: "The session is no longer valid. Please sign in again.",
          underlyingException: firebaseError,
          // Expected session-lifecycle outcome (token revoked — e.g. account
          // deleted or password changed elsewhere), not a Sentry error.
          reportToSentry: false,
        });
      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `An undefined Error happened: ${firebaseError.message}`,
          underlyingException: firebaseError,
        });
    }
  }
}
